<template>
    <div id="content">
        <div class="container">
            <div id="page-header">
                <ul class="breadcrumb">
                    <li class="icon"><a @click="$router.push({ name: 'parent_report' })"><img src="/themes/ito/images/back-to.svg" alt=""></a></li>
                    <li class="active">{{ $t('back_to_report') }}</li>
                </ul>
            </div>
            <div class="card fade-down">
                <div class="card-body">
                    <div class="card-title">
                        <span class="title">{{ $t('monthly_report_by_lesson_completion') }}</span>
                    </div>
                    <div class="card-content bg-light">
                        <div class="d-flex flex-column align-items-center justify-content-center text-center" v-if="errors.data">
                            <h5>{{ $t(errors.data) }} </h5>
                            <img class="mt-3" src="/themes/ito/images/mascot/isaac-extension.svg" alt="" width="125px">
                        </div>
                        <template v-else>
                            <div class="row">
                                <div class="col-8 ">
                                    <apexchart :options="chartOptions" :series="seriesData" @dataPointSelection="handleDataPointSelection" v-if="renderChart"></apexchart>
                                </div>
                                <div class="col-4">
                                    <div class="d-flex gap-2 w-100 p-3 flex-column align-items-center justify-content-center bg-white rounded-3 text-center">
                                        <template v-if="Object.keys(selectedData).length <= 0">
                                            <img src="/themes/ito/images/chart_icon.png" alt="chart">
                                            <p class="text-muted">{{ $t('click_to_view_details') }}</p>
                                        </template>

                                        <template v-else>
                                            <p>{{ $t('lesson_completion_by_month') + ' ' + selectedData.month }}</p>
                                            <p>iSMART {{ selectedData.subject }}</p>
                                            <div class="d-flex text-danger">
                                                <i class="ito-icon" :class="selectedData.completionRate != 0 ? 'ito-star-filled' : 'ito-star'"></i>
                                                <i class="ito-icon" :class="selectedData.completionRate >= 60 ? 'ito-star-filled' : 'ito-star'"></i>
                                                <i class="ito-icon" :class="selectedData.completionRate >= 80 ? 'ito-star-filled' : 'ito-star'"></i>
                                            </div>
                                            <div class="d-flex flex-column w-100 align-items-center gap-1 overflow-auto" style="max-height: 350px;">
                                                <div class="d-flex justify-content-between gap-3" v-for="(lesson, index) in selectedData.lessons" :key="index">
                                                    <span>{{ lesson.unit_name }}</span>
                                                    <span>{{ lesson.name }}</span>
                                                    <i class="ito-icon" :class="lesson.is_completed ? 'ito-check-circle' : 'ito-x-circle'"></i>
                                                </div>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import { getParentMonthlyReport } from "../../models/parent";

export default {
    name: "MonthlyReport",
    data() {
        return {
            renderChart: false,
            chartOptions: {
                chart: {
                    id: 'monthly-report',
                    zoom: {
                        enabled: true,
                        type: 'x',
                    }
                },
                colors: ['#FFAE58', '#4DBF72'],
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: '14px',
                        borderRadius: 4,
                        borderRadiusApplication: 'end',
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                tooltip: {
                    enabled: false,
                },
                stroke: {
                    show: true,
                    width: 2,
                    colors: ['transparent']
                },
                xaxis: {
                    labels: {
                        style: {
                            fontWeight: 'bold',
                            whiteSpace: 'normal',
                        },
                    },
                    categories: [],
                    axisBorder: {
                        show: true,
                        color: '#000000',
                        height: 1,
                        width: '100%',
                        offsetX: 0,
                        offsetY: 0
                    },
                    axisTicks: {
                        show: true,
                        borderType: 'solid',
                        color: '#000000',
                        height: 6,
                        offsetX: 0,
                        offsetY: 0
                    },
                },
                yaxis: {
                    min: 0,
                    max: 100,
                    tickAmount: 4,
                    labels: {
                        formatter: function (val) {
                            return val + "%";
                        },
                        style: {
                            fontWeight: 'bold',
                        },
                    },
                    axisBorder: {
                        show: true,
                        color: '#000000',
                        offsetX: 0,
                        offsetY: 0
                    },
                    axisTicks: {
                        show: true,
                        borderType: 'solid',
                        color: '#000000',
                        width: 6,
                        offsetX: -3,
                        offsetY: 0
                    },
                },
                fill: {
                    opacity: 1
                },
            },
            chartData: [],
            seriesData: [],
            selectedData: {},
            errors: {
                data: undefined,
            }
        }
    },
    created() {
        this.fetch();
    },
    methods: {
        fetch() {
            const that = this;
            that.$store.commit("setLoading", true);
            axios.all([getParentMonthlyReport()]).then(axios.spread((response) => {
                that.$store.commit("setLoading", false);
                if (response.data.code != 0) {
                    this.errors.data = response.data.msg;
                    return;
                }
                const chartData = response.data.results
                if (!chartData || chartData.length <= 0) {
                    this.errors.data = 'empty_data';
                    return;
                }
                this.chartData = chartData
                this.mapDataToChart();
                that.renderChart = true;
            }));
        },

        mapDataToChart() {
            const subjectsMap = {};
            this.chartData.forEach((report, index) => {
                report.subjects.forEach(subject => {
                    if (!subjectsMap[subject.id]) {
                        subjectsMap[subject.id] = {
                            name: subject.name,
                            type: 'column',
                            data: Array(this.chartData.length).fill(0),
                        };
                    }
                    subjectsMap[subject.id].data[index] = subject.lessons.filter(lesson => lesson.is_completed === 1).length / subject.lessons.length * 100;
                });
            });
            this.seriesData = Object.values(subjectsMap);
            this.chartOptions.xaxis.categories = this.chartData.map(report => [moment().month(report.month - 1).format('MMM'), `(${report.year})`]);
        },

        handleDataPointSelection(event, chartContext, { dataPointIndex, seriesIndex }) {
            const completedLessons = this.chartData[dataPointIndex].subjects[seriesIndex].lessons.filter(lesson => lesson.is_completed === 1).length;
            const totalLessons = this.chartData[dataPointIndex].subjects[seriesIndex].lessons.length;
            const completionRate = completedLessons / totalLessons * 100;
            this.selectedData = {
                month: moment().month(this.chartData[dataPointIndex].month + 1).format('MMM'),
                year: this.chartData[dataPointIndex].year,
                subject: this.seriesData[seriesIndex].name,
                lessons: this.chartData[dataPointIndex].subjects[seriesIndex].lessons,
                completedLessons: completedLessons,
                totalLessons: totalLessons,
                completionRate: completionRate
            }
        },
    }
}
</script>

<style scoped></style>